import React, { useMemo } from "react"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import OutlinedInput from "@mui/material/OutlinedInput"
import MenuItem from "@mui/material/MenuItem"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useSupplier } from "../../../../supplierHooks"
import { ListSubheader } from "@mui/material"
import {
    fetchSubscriptionsTypesForSupplierParams,
    SubscriptionsTypesForSupplierResponse,
    SupplierSubscriptionQuery,
} from "../../subscriptions/subscriptionsApi"
import { MenuProps } from "../../../../../../shared/components/filters/constants/filterMenuConst"

type MerchantSubscriptionsFilterProps = Readonly<{
    selectedIds: string[]
    setSelectedIds: (value: string[]) => void
    merchantIds?: string[]
}>

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

function transformData(data: SubscriptionsTypesForSupplierResponse) {
    return data.merchants
}

export function MultiMerchantSubscriptionsFilter({
    merchantIds,
    selectedIds,
    setSelectedIds,
}: MerchantSubscriptionsFilterProps) {
    const { classes } = useStyles()

    const { supplierId } = useSupplier()

    const { data } = useApiQuery(fetchSubscriptionsTypesForSupplierParams, {
        queryName: SupplierSubscriptionQuery.SUBSCRIPTIONS_TYPES_FOR_SUPPLIER,
        dependencies: {
            supplierId,
            merchantIds: merchantIds?.join(",") ?? "",
        },
        enabled: Boolean(merchantIds?.length),
        select: transformData,
    })

    const subscriptionsMap = useMemo(() => {
        const map: { [id: string]: { name: string; merchantName: string } } = {}
        data?.forEach((merchant) =>
            merchant.subscriptionTypes.forEach((subscription) => {
                map[subscription.id] = { name: subscription.displayName, merchantName: merchant.name }
            }),
        )
        return map
    }, [data])

    const handleMenuItemClick = (subscriptionId: string) => {
        const selectedIndex = selectedIds.indexOf(subscriptionId)
        let newSelectedIds: string[] = []

        if (selectedIndex === -1) {
            newSelectedIds = [...selectedIds, subscriptionId]
        } else {
            newSelectedIds = selectedIds.filter((id) => id !== subscriptionId)
        }

        setSelectedIds(newSelectedIds)
    }

    return (
        <FormControl className={classes.input}>
            <LocalizedStrict id="supplier-subscriptions-drawer-subscription-label">
                <InputLabel id="multiple-checkbox-subscriptions-label">Subscriptions</InputLabel>
            </LocalizedStrict>
            <Select
                labelId="multiple-checkbox-subscriptions-label"
                id="multiple-checkbox-subscriptions"
                multiple
                disabled={!Boolean(merchantIds?.length) || !data}
                value={selectedIds}
                input={<OutlinedInput label="Subscriptions" />}
                renderValue={(selected) =>
                    selected
                        .map(
                            (subscriptionId) =>
                                `${subscriptionsMap[subscriptionId].name} (${subscriptionsMap[subscriptionId].merchantName})` ||
                                "",
                        )
                        .join(", ")
                }
                MenuProps={MenuProps}
            >
                {data?.map((merchant) => (
                    <React.Fragment key={`${merchant.name}-header`}>
                        <ListSubheader>{merchant.name}</ListSubheader>
                        {merchant.subscriptionTypes.map((subscription) => (
                            <MenuItem
                                key={subscription.id}
                                value={subscription.id}
                                onClick={() => handleMenuItemClick(subscription.id.toString())}
                            >
                                <Checkbox checked={selectedIds.indexOf(subscription.id.toString()) > -1} />
                                <ListItemText primary={subscription.displayName} />
                            </MenuItem>
                        ))}
                    </React.Fragment>
                ))}
            </Select>
        </FormControl>
    )
}

